import { ChangeDetectionStrategy, Component, computed, linkedSignal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Report } from "../../pojo/Report";
import { ModalComponent } from "../modal/modal.component";
import { ModalHeaderComponent } from "../modal/modal.header.component";
import { ModalData } from "../modal/modal.service";
import type { ModalDefinition } from "../modal/modal.map";

export type ReportGroupByModalParams = {
    groupByConfigs: Report.GroupByConfig[];
    initialValues?: Record<string, string>;
};

@Component({
    selector: "report-group-by-modal",
    template: `
        <div class="s25-ng">
            <s25-modal-header [data]="headerData()"></s25-modal-header>
            <div class="modal-body">
                @for (groupByField of groupByConfigs(); track groupByField.fieldMapping) {
                    <div class="group-by-field">
                        <label [for]="fieldId(groupByField.fieldMapping)">{{ groupByField.parameterName }}</label>
                        <select
                            [id]="fieldId(groupByField.fieldMapping)"
                            class="cn-form__control group-by-select"
                            [ngModel]="selectedValues()[groupByField.fieldMapping]"
                            (ngModelChange)="setSelectedValue(groupByField.fieldMapping, $event)"
                        >
                            @for (groupByOption of groupByField.options; track groupByOption) {
                                <option [ngValue]="groupByOption">{{ groupByOption }}</option>
                            }
                        </select>
                    </div>
                }
                <div class="footer-buttons">
                    <button type="button" class="aw-button aw-button--outline" (click)="cancel()">Cancel</button>
                    <button type="button" class="aw-button aw-button--primary" (click)="confirm()">Export</button>
                </div>
            </div>
        </div>
    `,
    styles: `
        .group-by-field + .group-by-field {
            margin-top: 1rem;
        }

        .group-by-select {
            width: 100%;
            margin-top: 0.5rem;
        }

        .footer-buttons {
            display: flex;
            justify-content: flex-end;
            gap: 0.5rem;
            margin-top: 1.5rem;
        }
    `,
    standalone: true,
    imports: [ModalHeaderComponent, FormsModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportGroupByModalComponent extends ModalComponent<ReportGroupByModalParams> {
    static size = "sm" satisfies ModalDefinition["size"];

    readonly groupByConfigs = computed(() => this.params()?.groupByConfigs ?? []);

    readonly selectedValues = linkedSignal(() => {
        const modalParams = this.params();
        if (!modalParams) return {};
        return Object.fromEntries(
            modalParams.groupByConfigs.map((config) => [
                config.fieldMapping,
                modalParams.initialValues?.[config.fieldMapping] ?? config.defaultValue,
            ]),
        );
    });

    readonly headerData = (): ModalData => ({
        ...this.data,
        title: "Group search results by",
    });

    fieldId(fieldMapping: string): string {
        return `report-group-by-${fieldMapping}`;
    }

    setSelectedValue(fieldMapping: string, value: string): void {
        this.selectedValues.update((current) => ({ ...current, [fieldMapping]: value }));
    }

    confirm(): void {
        const selections = this.groupByConfigs().map((field) => ({
            fieldMapping: field.fieldMapping,
            value: this.selectedValues()[field.fieldMapping] ?? field.defaultValue,
        }));
        this.data.closeModal(selections);
    }

    cancel(): void {
        this.data.closeModal(undefined);
    }
}
